import { RecordTuple } from "record-tuple";

import { PaymentMethod as CanadaPaymentMethodPb } from "@repo/protobuf/gen/kikoff_canada/protobuf/models/payments/payment_method_pb";
import {
  PaymentMethod as UsPaymentMethodPb,
  PaymentType as UsPaymentTypePb,
} from "@repo/protobuf/gen/web/public/payment_methods_pb";
import Telemetry from "@repo/telemetry";
import Case from "@repo/utils/Case";
import Enum from "@repo/utils/Enum";
import Icon from "@repo/utils/Icon";
import Log from "@repo/utils/Log";
import UBoolean from "@repo/utils/UBoolean";
import UString from "@repo/utils/UString";

import Account from "../Account.js";

type PaymentMethod = {
  id: PaymentMethod.Id;

  affiliatedAccounts: {
    id: string;
    type: "creditLine";
    usage: "autopay";
  }[];
} & (
  | {
      type: "card";
      details: {
        brand: string;
        lastFour: string;
      };
    }
  | { type: "unknown"; details: null }
);

namespace PaymentMethod {
  export type Id = string & {};
  export const types = ["unknown", "card"] as const;
  export type Type = (typeof types)[number];

  export namespace Type {
    const iconByType = {
      card: "",
      unknown: "",
    } as const satisfies Record<Type, Icon>;
    export const icon = (type: Type | undefined) => type && iconByType[type];
  }

  export type AffiliatedAccount = Account.Ref & {
    usage: AffiliatedAccount.Usage;
  };
  export namespace AffiliatedAccount {
    export type Id = string & {};
    export type Type = "creditLine";
    export type Usage = "autopay";
  }

  export const format = Object.assign(
    (paymentMethod: PaymentMethod | undefined) => {
      if (!paymentMethod) return "-";

      if (paymentMethod.type === "card") {
        const { brand, lastFour } = paymentMethod.details;
        return `${UString.capitalize(brand)} ••••${lastFour}`;
      }

      return "Unknown";
    },
    {
      withIcon(paymentMethod: PaymentMethod) {
        if (!paymentMethod) return "-";

        // eslint-disable-next-line no-irregular-whitespace
        return `${Type.icon(paymentMethod.type)} ${format(paymentMethod)}`;
      },
    }
  );

  export const hasAffiliation = (
    paymentMethod: PaymentMethod,
    affiliatedAccount: AffiliatedAccount
  ) =>
    paymentMethod.affiliatedAccounts.some(
      (account) => RecordTuple(affiliatedAccount) === RecordTuple(account)
    );
}

export default PaymentMethod;

export namespace PaymentMethodUsa {
  export function normalize(
    paymentMethod: UsPaymentMethodPb
  ): PaymentMethod | undefined {
    if (paymentMethod.type !== UsPaymentTypePb.CARD) {
      // TODO: Support all payment method types
      Telemetry.captureError(
        new Error(
          `Only the following payment method types are supported: ${PaymentMethod.types.join(", ")}`
        )
      );
      return;
    }

    const [brand, lastFour] = paymentMethod.description.split("****");

    return {
      id: paymentMethod.token,
      type: "card",
      details: { brand, lastFour: lastFour! },
      affiliatedAccounts: paymentMethod.affiliations
        .map((account) => {
          const type = Case.fromConstant(
            Enum.keyOf(UsPaymentMethodPb.Affiliation.Type, account.type)
          ).toCamel();
          const usage = Case.fromConstant(
            Enum.keyOf(UsPaymentMethodPb.Usage, account.usage)
          ).toCamel();

          if (type !== "creditLine") {
            // TODO: Support all affiliated account types
            Log.error(
              `Unsupported affiliated account type ${type}, only creditLine is allowed.`
            );
            return;
          }
          if (usage !== "autopay") {
            // TODO: Support all affiliated account types
            Log.error(
              `Unsupported affiliated account usage ${type}, only autopay is allowed.`
            );
            return;
          }

          return { id: account.token, type, usage };
        })
        .filter(UBoolean.isTruthy),
    };
  }
}

export namespace PaymentMethodCanada {
  export function normalize(
    paymentMethod: CanadaPaymentMethodPb
  ): PaymentMethod {
    if (paymentMethod.type === CanadaPaymentMethodPb.Type.UNKNOWN)
      throw new Error("Unexpected UNKNOWN payment method type");

    return {
      id: paymentMethod.id,
      type: "card",
      details: paymentMethod.details.value!,
      affiliatedAccounts: paymentMethod.affiliatedAccounts.map((account) => {
        const type = Case.fromConstant(
          Enum.keyOf(CanadaPaymentMethodPb.AffiliatedAccount.Type, account.type)
        ).toCamel();
        const usage = Case.fromConstant(
          Enum.keyOf(
            CanadaPaymentMethodPb.AffiliatedAccount.Usage,
            account.usage
          )
        ).toCamel();

        if (type === "unknown")
          throw new Error("Unexpected UNKNOWN affiliated account type.");
        if (usage === "none")
          throw new Error("Unexpected NONE affiliated account usage.");

        return { id: account.id, type, usage };
      }),
    };
  }
}
